import {
  PROGRESS_SCHEMA_VERSION,
  PROGRESS_STORAGE_KEY,
  ResourceBudget,
} from "../domain/config";
import { AppError, ErrorClassification } from "../domain/errors";
import {
  AnswerOutcome,
  Domain,
  ExamMode,
  MasteryState,
  ProgressEventType,
  Track,
  type ProgressEvent,
} from "../domain/types";
import { traceSync } from "./trace";

const PROGRESS_APPEND_OPERATION = "progress_event.append";

export interface StorageBoundary {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export class LocalProgressStore {
  readonly #storage: StorageBoundary;

  constructor(storage: StorageBoundary) {
    this.#storage = storage;
  }

  load(): readonly ProgressEvent[] {
    const serialized = this.#storage.getItem(PROGRESS_STORAGE_KEY);
    if (serialized === null) {
      return [];
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(serialized) as unknown;
    } catch (error: unknown) {
      throw new AppError(
        "Saved progress is not valid JSON.",
        ErrorClassification.PERMANENT,
        "INVALID_PROGRESS_JSON",
        error instanceof Error ? { cause: error } : undefined,
      );
    }

    if (!Array.isArray(parsed) || !parsed.every(isProgressEvent)) {
      throw new AppError(
        "Saved progress does not match the current schema.",
        ErrorClassification.PERMANENT,
        "INVALID_PROGRESS_SCHEMA",
      );
    }

    return parsed;
  }

  append(event: ProgressEvent): readonly ProgressEvent[] {
    if (!isProgressEvent(event)) {
      throw new AppError(
        "Attempt completion event failed boundary validation.",
        ErrorClassification.PERMANENT,
        "INVALID_PROGRESS_EVENT",
      );
    }

    return traceSync(event.traceId, PROGRESS_APPEND_OPERATION, () => {
      const existingEvents = this.load();
      const duplicate = existingEvents.some(
        (existingEvent) =>
          existingEvent.eventId === event.eventId ||
          existingEvent.attemptId === event.attemptId,
      );

      if (duplicate) {
        return existingEvents;
      }

      const nextEvents = [...existingEvents, event];
      const serialized = JSON.stringify(nextEvents);
      const serializedBytes = new TextEncoder().encode(serialized).byteLength;

      if (serializedBytes > ResourceBudget.MAX_PROGRESS_BYTES) {
        throw new AppError(
          "Saved progress exceeds the configured local storage budget.",
          ErrorClassification.PERMANENT,
          "PROGRESS_BUDGET_EXCEEDED",
        );
      }

      try {
        this.#storage.setItem(PROGRESS_STORAGE_KEY, serialized);
      } catch (error: unknown) {
        throw new AppError(
          "The browser could not save progress. Check site storage permissions and available space.",
          ErrorClassification.PERMANENT,
          "PROGRESS_WRITE_FAILED",
          error instanceof Error ? { cause: error } : undefined,
        );
      }
      return nextEvents;
    });
  }
}

function isProgressEvent(value: unknown): value is ProgressEvent {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.schemaVersion === PROGRESS_SCHEMA_VERSION &&
    value.eventType === ProgressEventType.ATTEMPT_COMPLETED &&
    isNonEmptyString(value.eventId) &&
    isNonEmptyString(value.attemptId) &&
    isNonEmptyString(value.traceId) &&
    isIsoDate(value.occurredAt) &&
    isEnumValue(ExamMode, value.mode) &&
    isNonEmptyString(value.questionBankVersion) &&
    isScoreReport(value.report)
  );
}

function isScoreReport(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  if (
    !isNonNegativeInteger(value.correct) ||
    !isPositiveInteger(value.total) ||
    value.correct > value.total ||
    !isPercentage(value.percentage) ||
    value.percentage !== Math.round((value.correct / value.total) * 100) ||
    !isEnumValue(MasteryState, value.mastery) ||
    !Array.isArray(value.domainScores) ||
    !value.domainScores.every(isDomainScore) ||
    !Array.isArray(value.results) ||
    value.results.length !== value.total ||
    !value.results.every(isQuestionResult)
  ) {
    return false;
  }

  return (
    value.results.filter(
      (result) =>
        isRecord(result) && result.outcome === AnswerOutcome.CORRECT,
    ).length === value.correct
  );
}

function isDomainScore(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isEnumValue(Domain, value.domain) &&
    isNonNegativeInteger(value.correct) &&
    isPositiveInteger(value.total) &&
    value.correct <= value.total &&
    isPercentage(value.percentage) &&
    value.percentage === Math.round((value.correct / value.total) * 100) &&
    isEnumValue(MasteryState, value.mastery)
  );
}

function isQuestionResult(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNonEmptyString(value.questionId) &&
    isEnumValue(Track, value.track) &&
    isEnumValue(Domain, value.domain) &&
    (value.selectedChoiceId === null || isNonEmptyString(value.selectedChoiceId)) &&
    isNonEmptyString(value.correctChoiceId) &&
    isEnumValue(AnswerOutcome, value.outcome)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isIsoDate(value: unknown): value is string {
  return (
    isNonEmptyString(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString() === value
  );
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && typeof value === "number" && value >= 0;
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && typeof value === "number" && value > 0;
}

function isPercentage(value: unknown): value is number {
  return typeof value === "number" && value >= 0 && value <= 100;
}

function isEnumValue<T extends Readonly<Record<string, string>>>(
  enumObject: T,
  value: unknown,
): value is T[keyof T] {
  return typeof value === "string" && Object.values(enumObject).includes(value);
}
